<template>
  <div 
    :style="{
      'width': '100%',
      'margin': `0 auto`,
      'padding': isMobile ? '0' : `0px calc(48px + ${curNavigationOffset}px)`,
      'overflow-x': `hidden`,
    }"
  >
    <div class="scount-slider">
      
      <div :class="`scount-slider__container ${swiperContainerClass}`" :style="getContainerStyles()">

        <div v-if="!noNavigation || forcedNavigation" class="scount-slider__buttons" :style="getNavigationStyles()">
          <Button
            v-show="numSlides > 1 || forcedNavigation"
            ref="navigationLeft"
            class="swiper-button-prev"
            :class="{ 'swiper-button_forced': forcedNavigation }"
            variant="clear"
            shape="circle"
            size="lg"
            iconLeft="left"
            @click="onNavigation('left')"
          ></Button>
          <Button
            v-show="numSlides > 1 || forcedNavigation"
            ref="navigationRight"
            class="swiper-button-next"
            :class="{ 'swiper-button_forced': forcedNavigation }"
            variant="clear"
            shape="circle"
            size="lg"
            iconLeft="right"
            @click="onNavigation('right')"
          ></Button>
        </div>      

        <div 
          class="scount-slider__wrapper swiper-wrapper"
          :class="{ 'wrapper_single-row': curRows === 1 }"
          ref="wrapper"
        >
          <slot></slot>
        </div>

        <div v-if="!noPagination" v-show="numSlides > 1" class="swiper-pagination" :style="{ 'top': `calc(16px + ${curPaginationOffset}px)` }"></div>
        <div v-else class="swiper-pagination" :style="{ 'top': `calc(16px + ${curPaginationOffset}px)` }"></div>

      </div>
          
    </div>
  </div>
</template>

<script>
import Button from "@rr-component-library/button/src/main";
import { debounce } from "../assets/js/helpers";
import Swiper, { Pagination, Navigation, Autoplay } from "swiper";

Swiper.use([Pagination, Navigation, Autoplay]);

export default {
  name: 'Slider',
  props: {
    /**
     * Количество колонок<br>
     * Строка: `'Number'`<br>
     * Объект: `{ default: '1', md: '2', lg: '3', xl: '4' }`
     */
    cols: {
      type: [String, Object],
      validator(value) {
        if (!value) {
          return true;
        }
        if (typeof value === 'string') {
          return value.match(/(\d+)/);
        }
        if (typeof value === 'object') {
          return !Object.keys(value).some((val) => !value[val].match(/(\d+)/));
        }
        return false;
      },
      default: '1',
    },
    /**
     * Количество строк<br>
     * Строка: `'Number'`<br>
     * Объект: `{ default: '1', md: '2', lg: '3', xl: '4' }`
     */
    rows: {
      type: [String, Object],
      validator(value) {
        if (!value) {
          return true;
        }
        if (typeof value === 'string') {
          return value.match(/(\d+)/);
        }
        if (typeof value === 'object') {
          return !Object.keys(value).some((val) => !value[val].match(/(\d+)/));
        }
        return false;
      },
      default: '1',
    },
    /**
     * Дополнительное смещение кнопок навигации в стороны от области контента, px<br>
     * Строка: `'Number'`<br>
     * Объект: `{ default: '0', md: '32', lg: '32', xl: '48' }`
     */
    navigationOffset: {
      type: [String, Object],
      validator(value) {
        if (!value) {
          return true;
        }
        if (typeof value === 'string') {
          return value.match(/(\d+)/);
        }
        if (typeof value === 'object') {
          return !Object.keys(value).some((val) => !value[val].match(/(\d+)/));
        }
        return false;
      },
      default: '0',
    },
    /**
     * Дополнительное смещение пагинации вниз от области контента, px<br>
     * Строка: `'Number'`<br>
     * Объект: `{ default: '0', md: '48', lg: '48', xl: '64' }`
     */
    paginationOffset: {
      type: [String, Object],
      validator(value) {
        if (!value) {
          return true;
        }
        if (typeof value === 'string') {
          return value.match(/(\d+)/);
        }
        if (typeof value === 'object') {
          return !Object.keys(value).some((val) => !value[val].match(/(\d+)/));
        }
        return false;
      },
      default: '0',
    },
    /**
     * Автоматическое листание слайдов по таймеру. При отсутствии параметра - отключено.
     * Объект: `{
        delay: 7000,
        stopOnLastSlide: false,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }`
     */
    autoplay: {
      type: Object,
    },
    /**
     * Отображать в слайдере только элементы с указанным значением атрибута data-group-id
     */
    filterByGroupId: {
      type: String,
      default: '',
    },
    /**
     * Сколько слайдов отображать в области просмотра
     */
    slidesPerView: {
      type: Number,
      default: 1.25,
    },
    /**
     * Индекс слайда, отображаемого первоначально
     */
    initialSlide: {
      type: Number,
      default: 0,
    },
    /**
     * Показывать слайды по кругу ("карусель")
     */
    loop: {
      type: Boolean,
    },
    /**
     * Не показывать кнопки навигации
     */
    noNavigation: {
      type: Boolean,
    },
    /**
     * Не показывать пагинацию
     */
    noPagination: {
      type: Boolean,
    },
    /**
     * Подстраивать высоту слайдера под высоту текущего слайда
     */
    autoHeight: {
      type: Boolean,
    },
    forcedNavigation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Button,
  },
  data() {
    return {
      isFirstRun: true,
      isMobile: false,
      isTabletPortrait: false,
      isTabletLandscape: false,
      isDesktop: false,
      numSlides: 0,
      numItems: 0,
      items: [],
      swiperContainerClass: `swiper-container${Math.random().toString().replace('.', '')}`,
      swiper: null,
      swiperUpdateTimeout: null,
      curCols: 0,
      curRows: 0,
      curNavigationOffset: 0,
      curPaginationOffset: 0,
      isRecreating: false,
      debouncedUpdate: debounce(this.colsRowsUpdate, 100),
      debouncedRecreate: debounce(this.recreateSwiper, 100),
    };
  },
  computed: {
    itemsPerSlide() {
      return this.curCols * this.curRows;
    },
    itemsPadding() {
      if (this.isMobile) return 12;
      if (this.isTabletPortrait || this.isTabletLandscape) return 16;
      return 20;
    },
  },
  watch: {
    cols() {
      this.recreateSlides();
    },
    rows() {
      this.recreateSlides();
    },
    curCols() {
      this.$emit('cols-changed', this.curCols);
      if (this.debouncedUpdate && !this.isRecreating) this.debouncedUpdate();
    },
    curRows() {
      this.$emit('rows-changed', this.curRows);
      if (this.debouncedUpdate && !this.isRecreating) this.debouncedUpdate();
    },
    curNavigationOffset() {
      this.$emit('navigation-offset-changed', this.curNavigationOffset);
      if (this.debouncedRecreate && !this.isFirstRun) {
        this.isRecreating = true;
        this.debouncedRecreate();
      }
    },
    curPaginationOffset() {
      this.$emit('pagination-offset-changed', this.curPaginationOffset);
      if (this.debouncedRecreate && !this.isFirstRun) {
        this.isRecreating = true;
        this.debouncedRecreate();
      }
    },
    isMobile(value) {
      if (value) this.onBreakpointChanged('default');
    },
    isTabletPortrait(value) {
      if (value) this.onBreakpointChanged('md');
    },
    isTabletLandscape(value) {
      if (value) this.onBreakpointChanged('lg');
    },
    isDesktop(value) {
      if (value) this.onBreakpointChanged('xl');
    },
  },
  methods: {
    createSlides() {
      const { wrapper } = this.$refs;
      if (!wrapper) return;
      const children = wrapper.children;
      let items = [];
      for (let i = 0; i < children.length; i += 1) {
        items.push(children.item(i));
      }
      this.items = items;
      const filtered = this.filterItemsByGroup(items);

      const numSlides = Math.ceil(filtered.length / this.itemsPerSlide);
      this.numSlides = numSlides;
      this.numItems = filtered.length;

      let currentItem = 0;
      for (let i = 0; i < numSlides; i += 1) {
        const slide = document.createElement('div');
        slide.setAttribute('class', 'swiper-slide');
        for (let j = 0; j < this.itemsPerSlide; j += 1) {
          currentItem = i * this.itemsPerSlide + j;
          if (currentItem > filtered.length - 1) break;
          const item = filtered[currentItem];
          item.setAttribute('style', this.getSlideStyles(j));
          slide.appendChild(item);
        }
        wrapper.appendChild(slide);
      }
    },
    createSwiper() {
      const vm = this;
      setTimeout(() => { 
        this.swiper = new Swiper(`.${this.swiperContainerClass}`, {
          touchMoveStopPropagation: true,
          noSwipingSelector: '.swiper-button-prev, .swiper-button-next',
          pagination: !this.noPagination ? {
            el: '.swiper-pagination',
            type: 'bullets',
            clickable: true
          }
          : false,
          navigation: !this.noNavigation ? {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }
          : false,
          slidesPerView: this.isMobile ? (this.loop ? 1 : this.slidesPerView) : 1,
          slidesPerColumn: 1,
          initialSlide: this.initialSlide,
          loop: this.loop,
          autoHeight: this.autoHeight,
          autoplay: this.autoplay || false,
          on: {
            init: function() {
              vm.$emit('init');
              vm.isRecreating = false;
            },
            imagesReady: function() {
              vm.$emit('imagesReady');
            },
            update: function() {
              vm.$emit('update');
            },
            activeIndexChange: function(swiper) {
              vm.$emit('indexChanged', swiper.activeIndex);
              vm.updateNavigationEnabled();
            },
            destroy: function() {
              vm.$emit('destroy');
            },
          },
        });
      }, 100);
    },
    recreateSlides() {
      const { wrapper } = this.$refs;
      if (!wrapper) return;

      const children = wrapper.children;
      const itemsToRemove = [];
      for (let i = 0; i < children.length; i += 1) {
        if (!this.isItemHidden(children.item(i))) {
          itemsToRemove.push(children.item(i));
        }
      }

      const filtered = this.filterItemsByGroup(this.items);
      const numSlides = Math.ceil(filtered.length / this.itemsPerSlide);
      this.numSlides = numSlides;
      this.numItems = filtered.length;

      let currentItem = 0;
      for (let i = 0; i < numSlides; i += 1) {
        const slide = document.createElement('div');
        slide.setAttribute('class', 'swiper-slide');
        for (let j = 0; j < this.itemsPerSlide; j += 1) {
          currentItem = i * this.itemsPerSlide + j;
          if (currentItem > filtered.length - 1) break;
          const item = filtered[currentItem];
          item.setAttribute('style', this.getSlideStyles(j));
          slide.appendChild(item);
        }
        wrapper.appendChild(slide);
      }

      for (let i = 0; i < itemsToRemove.length; i += 1) {
        itemsToRemove[i].remove();
      }

      this.updateSwiper();
    },
    updateSwiper() {
      if (this.isRecreating) {
        clearTimeout(this.swiperUpdateTimeout);
        return;
      }
      if (this.swiper) {
        if (this.swiper.update && typeof this.swiper.update === 'function') {
          clearTimeout(this.swiperUpdateTimeout);
          setTimeout(() => this.swiper.update(), 50);
          return;
        }
      }
      this.swiperUpdateTimeout = setTimeout(() => this.updateSwiper, 50);
    },
    recreateSwiper() {
      if (this.swiper && this.swiper.destroy) {
        this.swiper.destroy(false, false);
        this.createSwiper();
      }
    },
    colsRowsUpdate() {
      if (this.isFirstRun) {
        this.isFirstRun = false;
        this.createSlides();
        this.createSwiper();
        return;
      }
      this.recreateSlides();
    },
    onResize() {
      this.isMobile = window.innerWidth < 768;
      this.isTabletPortrait = window.innerWidth >= 768 && window.innerWidth < 1024;
      this.isTabletLandscape = window.innerWidth >= 1024 && window.innerWidth < 1200;
      this.isDesktop = window.innerWidth >= 1200;
    },
    onBreakpointChanged(breakpoint) {
      this.curCols = this.getValueForBreakpoint(this.cols, breakpoint) || 1;
      this.curRows = this.getValueForBreakpoint(this.rows, breakpoint) || 1;
      this.curNavigationOffset = this.getValueForBreakpoint(this.navigationOffset, breakpoint);
      this.curPaginationOffset = this.getValueForBreakpoint(this.paginationOffset, breakpoint);
    },
    onNavigation(direction) {
      console.log(direction);
      this.$emit('navigation', direction);
    },
    getValueForBreakpoint(values, breakpoint) {
      if (typeof values === 'object') {
        if (typeof values[breakpoint] === 'string') {
          return this.getParsedInt(values[breakpoint]);
        } else if (typeof values['default'] === 'string') {
          return this.getParsedInt(values['default']);
        }
      } else if (typeof values === 'string') {
        return this.getParsedInt(values);
      } 
      return 0;
    },
    filterItemsByGroup(items) {
      const filtered = [];
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        const group = item.getAttribute('data-group-id');
        if (group === '' || this.filterByGroupId === '') {
          filtered.push(item);
          item.classList.remove('slider-item-hidden');
          continue;
        }
        if (group === this.filterByGroupId) {
          filtered.push(item);
          item.classList.remove('slider-item-hidden');
        } else {
          item.classList.add('slider-item-hidden');
        }
      }
      return filtered;
    },
    isItemHidden(item) {
      if (!item) return false;
      return item.classList.contains('slider-item-hidden');
    },
    getParsedInt(value) {
      try {
        const parsed = parseInt(value);
        if (isNaN(parsed)) return 0;
        return parsed;
      } catch(e) {
        return 0;
      }
    },
    getContainerStyles() {
      return `padding-bottom: calc(16px + ${this.curPaginationOffset}px;`;
    },
    getSlideStyles(index) {
      let styles = '';
      const padding = this.itemsPadding * (this.curCols - 1);
      styles = `width: calc((100% - ${padding}px) / ${this.curCols}); `;
      if (this.isLastCol(index) && (!this.isMobile || (this.isMobile && this.loop))) {
        styles += 'margin-right: 0; ';
      }
      if (this.isLastRow(index)) {
        styles += 'margin-bottom: 0; ';
      }
      return styles;
    },
    getNavigationStyles() {
      return {
        width: `calc(100% + 96px + ${this.curNavigationOffset * 2}px)`,
        left: `calc(-48px - ${this.curNavigationOffset}px)`,
        height: `calc(100% - 32px - ${this.curPaginationOffset}px)`,
      };
    },
    isLastCol(index) {
      if (this.curCols === 1) {
        return true;
      } else if (this.curRows === 1) {
        if (index + 1 === this.curCols * this.curRows) return true;
      } else {
        if ((index + 1) % this.curCols === 0) return true;
      }
      return false;
    },
    isLastRow(index) {
      if (this.curRows === 1) {
        return true;
      } else if (this.curCols === 1) {
        if (index + 1 === this.curCols * this.curRows) return true;
      } else {
        if ((index + 1) > this.curCols * (this.curRows - 1)) return true;
      }
      return false;
    },
    updateNavigationEnabled() {
      if (!this.forcedNavigation) return;
      const left = this.$refs.navigationLeft;
      const right = this.$refs.navigationRight;
      setTimeout(() => {
        if (left && left.$el.attributes.getNamedItem('disabled')) left.$el.attributes.removeNamedItem('disabled');
        if (right && right.$el.attributes.getNamedItem('disabled')) right.$el.attributes.removeNamedItem('disabled');
      }, 100);
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.swiperUpdateTimeout);
  },
};
</script>

<style lang="scss" scoped>
  @import "~swiper/swiper";
  @import "./Slider";
</style>
